// Subir a un elemento sin que el header fijo le tape la primera linea.
//
// Antes esto se hacia con `scrollIntoView({ block: "center" })` repartido en
// tres componentes. Centrar funciona con mensajes cortos, pero la tabla de la
// planificacion y la seccion de slides miden varias pantallas: centrarlas
// dejaba al profesor en la mitad del bloque, pasada la tabla, mirando espacio
// en blanco.
//
// La altura del header se mide en vez de fijarse, porque cambia entre escritorio
// y telefono.

const HEADER_SELECTOR = ".header-general-container";
const FALLBACK_HEADER = 59;
const BREATHING_ROOM = 12;

/** Alto del header fijo, medido en vivo. */
export function headerOffset() {
  if (typeof document === "undefined") return FALLBACK_HEADER;
  const header = document.querySelector(HEADER_SELECTOR);
  if (!header || !header.getBoundingClientRect) return FALLBACK_HEADER;
  const alto = Math.round(header.getBoundingClientRect().height);
  // Si el header esta oculto (alto 0) el fallback evita pegar el texto arriba.
  return (alto || FALLBACK_HEADER) + BREATHING_ROOM;
}

/**
 * Deja `el` justo debajo del header. No hace nada si el elemento no existe,
 * porque los llamadores lo buscan por id/ref y puede no estar montado todavia.
 */
export function scrollToElementTop(el) {
  if (!el || !el.getBoundingClientRect || typeof window === "undefined") return;
  const top = el.getBoundingClientRect().top + window.pageYOffset - headerOffset();
  window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
}

/** Final real de la pagina (el del documento, no el de un contenedor). */
export function scrollToPageBottom() {
  if (typeof window === "undefined") return;
  window.scrollTo({
    top: document.documentElement.scrollHeight,
    behavior: "smooth",
  });
}

/**
 * Sigue el final de un bloque que va creciendo (la tabla mientras streamea),
 * para que el profesor vea construirse el contenido sin tener que bajar a mano.
 *
 * Dos reglas para no ser molesto:
 *  - si el usuario sube a releer, se deja de seguir hasta que el vuelva al final
 *  - si el final ya esta a la vista, no se toca el scroll
 *
 * `estado` es un objeto que el llamador conserva entre llamadas
 * (`{ usuarioSeFue: false, pendiente: false }`).
 */
export function followContentBottom(el, estado) {
  if (!el || !el.getBoundingClientRect || typeof window === "undefined") return;
  if (estado.usuarioSeFue || estado.pendiente) return;

  estado.pendiente = true;
  window.requestAnimationFrame(() => {
    estado.pendiente = false;
    const fin = el.getBoundingClientRect().bottom;
    const alto = window.innerHeight;
    // Margen para que el ultimo parrafo no quede pegado al borde inferior.
    const COLCHON = 140;
    if (fin <= alto - 20) return; // el final ya se ve, no hay nada que seguir
    window.scrollTo({
      top: window.pageYOffset + (fin - alto) + COLCHON,
      behavior: "smooth",
    });
  });
}

/**
 * Marca que el usuario tomo el control del scroll. Devuelve la funcion de
 * limpieza para el `beforeDestroy` del componente.
 */
export function watchUserScroll(estado) {
  if (typeof window === "undefined") return () => {};

  // Cuanto hay que subir, acumulado, para que se entienda como "quiero releer".
  // Un solo pixel no basta: los trackpads producen deltas hacia arriba todo el
  // tiempo por la inercia, y con el umbral en cero el seguimiento se apagaba
  // apenas el usuario tocaba el mouse, para no volver nunca.
  const UMBRAL_SUBIDA = 90;
  // Zona del final donde se considera que el usuario "esta al dia" y se retoma
  // el seguimiento. Ojo con agrandarla: si es mayor que lo que el usuario
  // acaba de subir, al subir para releer sigue dentro de la zona y el
  // seguimiento se reactiva al instante, arrastrandolo de vuelta.
  const ZONA_FONDO = 260;
  // Bajar deliberadamente tambien es senal de "quiero seguir el hilo", y sirve
  // de segunda via de vuelta sin tener que llegar exacto al final.
  const UMBRAL_BAJADA = 400;

  estado.subidaAcumulada = 0;
  estado.bajadaAcumulada = 0;

  const alRodar = (e) => {
    if (e.deltaY === undefined) return;
    if (e.deltaY < 0) {
      estado.subidaAcumulada += -e.deltaY;
      if (estado.subidaAcumulada >= UMBRAL_SUBIDA) estado.usuarioSeFue = true;
    } else if (e.deltaY > 0) {
      estado.subidaAcumulada = 0;
      estado.bajadaAcumulada += e.deltaY;
      if (estado.bajadaAcumulada >= UMBRAL_BAJADA) {
        estado.usuarioSeFue = false;
        estado.bajadaAcumulada = 0;
      }
    }
  };

  const alScrollear = () => {
    const restante =
      document.documentElement.scrollHeight -
      window.innerHeight -
      window.pageYOffset;
    if (restante <= ZONA_FONDO) {
      estado.usuarioSeFue = false;
      estado.subidaAcumulada = 0;
      estado.bajadaAcumulada = 0;
    }
  };

  window.addEventListener("wheel", alRodar, { passive: true });
  window.addEventListener("scroll", alScrollear, { passive: true });
  return () => {
    window.removeEventListener("wheel", alRodar);
    window.removeEventListener("scroll", alScrollear);
  };
}

/**
 * Mantiene a la vista el bloque que se esta construyendo AHORA (una slide),
 * llevando su inicio justo debajo del header.
 *
 * Se diferencia de `followContentBottom`, que persigue el final de un bloque
 * que crece: eso sirve para un texto largo como la tabla, pero con las slides
 * dejaba al profesor mirando el borde de abajo —o peor, la burbuja de carga
 * que vive despues—. Aca lo que importa es la diapositiva, no su final.
 *
 * No hace nada si el usuario tomo el control del scroll, ni si el bloque ya
 * esta comodo en pantalla (para no reposicionar en cada trozo del stream).
 */
export function followElementTop(el, estado) {
  if (!el || !el.getBoundingClientRect || typeof window === "undefined") return;
  if (estado.usuarioSeFue || estado.pendiente) return;

  const r = el.getBoundingClientRect();
  const tope = headerOffset();
  const alto = window.innerHeight;

  // Ya se ve bien: su inicio esta bajo el header y hay contenido en pantalla.
  const yaComodo = r.top >= tope - 8 && r.top < alto * 0.55;
  if (yaComodo) return;

  estado.pendiente = true;
  window.requestAnimationFrame(() => {
    estado.pendiente = false;
    scrollToElementTop(el);
  });
}

/**
 * Deja la vista justo debajo de la fila de burbujas de servicios: al entrar a
 * un servicio (o a su demo) lo primero que se ve es el chat, no el menu por el
 * que el usuario acaba de pasar.
 *
 * Antes cada camino de entrada hacia `scrollIntoView()` sobre las burbujas, o
 * no hacia nada, asi que cada servicio abria en una posicion distinta y con el
 * menu ocupando la pantalla.
 */
export function scrollBelowServiceBubbles() {
  if (typeof document === "undefined" || typeof window === "undefined") return;
  const bubbles = document.getElementById("buttons");
  if (!bubbles) return;
  const top =
    bubbles.getBoundingClientRect().bottom + window.pageYOffset - headerOffset();
  window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
}
